import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { debounceTime, filter, Subject, take, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthCognitoService } from '../../auth/auth-cognito.service';
import { subjectLabels, subjectWord } from '../../configuration/subject-label.util';
import { ContactService } from '../../notification/contact.service';
import { NotificationViewModel } from '../../notification/notification.view-model';
import { buildStandalonePatientSelfContact } from '../../notification/standalone-patient-contact';
import { DashboardSectionHeaderComponent } from '../../notification/dashboard/dashboard-section-header/dashboard-section-header.component';
import { MaterialModule } from '../../shared/material/material.module';
import { SkeletonComponent } from '../../shared/skeleton/skeleton.component';
import { FormUtils } from '../../shared/utils/form-utils';
import { v4 as uuid } from 'uuid';
import { AddPersonDialogComponent } from '../add-person-dialog/add-person-dialog.component';
import { patientsHeader } from '../callback-location.util';
import { ContactsViewModel } from '../contacts.view-model';
import { findExactPatientMatch } from '../patient-match.util';
import { Patient } from '../patient.model';
import { PatientService } from '../patient.service';
import { OrganizationsComponent } from '../organizations/organizations.component';
import { Organization, UpdateProfileRequest } from '../user.service';
import { AlertRosterUploadWizardComponent } from './alert-roster-upload-wizard.component';

const PATIENTS_PAGE_SIZE = 50;

@Component({
  selector: 'app-alert-roster',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatSidenavModule,
    ReactiveFormsModule,
    DashboardSectionHeaderComponent,
    OrganizationsComponent,
    SkeletonComponent,
  ],
  templateUrl: './alert-roster.component.html',
  styleUrl: './alert-roster.component.scss',
  host: { class: 'contact-list' },
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AlertRosterComponent implements OnInit, OnDestroy {
  #router = inject(Router);
  #dialog = inject(MatDialog);
  #snackBar = inject(MatSnackBar);
  #authCognitoService = inject(AuthCognitoService);
  #patientService = inject(PatientService);
  #contactService = inject(ContactService);
  #contactsViewModel = inject(ContactsViewModel);
  #notificationViewModel = inject(NotificationViewModel);

  @ViewChild('drawer', { static: true })
  organizationMenu!: MatSidenav;

  patients = signal<Patient[]>([]);
  patientsLoading = signal(true);
  locations = toSignal(this.#contactsViewModel.locations$, { initialValue: [] });
  locationsLoading = toSignal(this.#contactsViewModel.locationsLoading$);
  profile$ = this.#contactsViewModel.profile$;
  profileError$ = this.#contactsViewModel.profileError$;
  profile = toSignal(this.profile$);
  currentOrganization = toSignal(this.#contactsViewModel.currentOrganization$);

  #nextPageToken = signal<string | undefined>(undefined);
  /** Page tokens: undefined = first page; otherwise API `nextPage` used to fetch that page. */
  #pageTokens: (string | undefined)[] = [undefined];
  #pageIndex = 0;

  searchForm = new FormGroup({
    search: new FormControl<string>('', { nonNullable: true }),
  });

  destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.searchForm.controls.search.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.#pageIndex = 0;
      this.#pageTokens = [undefined];
      this.loadPatientsPage();
    });

    this.profile$
      .pipe(
        filter((p) => !!p),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.#contactsViewModel.loadLocations();
        this.#pageIndex = 0;
        this.#pageTokens = [undefined];
        this.loadPatientsPage();
      });

    this.profile$
      .pipe(
        take(1),
        filter((profile) => !profile),
      )
      .subscribe(() => {
        this.#contactsViewModel.loadProfile();
      });

    this.profileError$
      .pipe(
        takeUntil(this.destroy$),
        filter((e) => !!e),
      )
      .subscribe(() => {
        const ref = this.#snackBar.open("Sorry, we couldn't load the user information.", 'Okay', {
          duration: 5000,
        });
        ref.afterDismissed().subscribe(() => this.onLogout());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  patientsHeaderText(): string {
    const { singular, plural } = subjectLabels(this.currentOrganization());
    if (this.patientsLoading() === true) {
      return patientsHeader(0, true, singular, plural);
    }
    // Match the list query: non-archived patients only. Org `patientsCount` includes archived
    // records and does not decrement on archive, so it must not drive this header.
    const n = this.patients().length;
    const countLabel = this.hasNextPage() ? `${n}+` : String(n);
    if (n === 1 && !this.hasNextPage()) {
      return `1 ${singular}`;
    }
    return `${countLabel} ${plural}`;
  }

  loadPatientsPage(): void {
    this.patientsLoading.set(true);
    const search = this.searchForm.controls.search.value.trim();
    const nextPage = this.#pageTokens[this.#pageIndex];

    this.#patientService
      .getPatients({
        pageSize: PATIENTS_PAGE_SIZE,
        sort: 'updatedDesc',
        search: search || undefined,
        nextPage,
      })
      .pipe(take(1))
      .subscribe({
        next: ({ result, nextPage: token }) => {
          this.patients.set(result);
          this.#nextPageToken.set(token);
          this.patientsLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading patients', err);
          this.patients.set([]);
          this.patientsLoading.set(false);
        },
      });
  }

  hasNextPage(): boolean {
    return Boolean(this.#nextPageToken());
  }

  hasPreviousPage(): boolean {
    return this.#pageIndex > 0;
  }

  onNextPage(): void {
    const token = this.#nextPageToken();
    if (!token) {
      return;
    }
    this.#pageIndex += 1;
    if (this.#pageTokens.length <= this.#pageIndex) {
      this.#pageTokens.push(token);
    }
    this.loadPatientsPage();
  }

  onPreviousPage(): void {
    if (!this.hasPreviousPage()) {
      return;
    }
    this.#pageIndex -= 1;
    this.loadPatientsPage();
  }

  onAddNew(): void {
    const { singular, word } = this.#subject();
    const ref = this.#dialog.open(AddPersonDialogComponent, {
      data: { locations: this.locations(), subjectLabel: singular },
    });
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.#patientService
        .getPatients({ includeArchived: true, pageSize: 100, sort: 'updatedDesc' })
        .pipe(take(1))
        .subscribe({
          next: ({ result: allPatients }) => {
            const match = findExactPatientMatch(allPatients, result.name, result.phone);
            if (match?.archived) {
              this.#unarchivePatient(match);
              return;
            }
            if (match) {
              this.#offerOpenExistingPatient(
                match,
                `${singular} already exists. Please open the ${word} to alert.`,
              );
              return;
            }
            this.#createNewPatient(result);
          },
          error: () => this.#createNewPatient(result),
        });
    });
  }

  onSelectedPatient(patient: Patient): void {
    const currentPatientId = sessionStorage.getItem('patientId');
    if (currentPatientId !== patient.id) {
      sessionStorage.setItem('patientId', patient.id);
      this.#notificationViewModel.resetState();
    }

    void this.#router.navigate(['/contact'], {
      queryParams: {
        patientId: patient.id,
        patientName: patient.name,
        providerId: patient.locationId,
      },
    });
  }

  onOrganizationSelected(org: Organization): void {
    this.organizationMenu.toggle();
    if (org.id !== this.profile()?.currentOrg) {
      const args: UpdateProfileRequest = {
        name: this.profile()?.name || '',
        currentOrg: org.id,
      };
      this.#contactsViewModel.updateOrganization(args, org);
      this.searchForm.patchValue({ search: '' }, { emitEvent: false });
      this.#pageIndex = 0;
      this.#pageTokens = [undefined];
    }
  }

  onLogout(): void {
    void this.#authCognitoService.signOut().then(() => {
      sessionStorage.removeItem('patientId');
      sessionStorage.removeItem('organizationId');
    });
  }

  formatPhone(phone: string | undefined): string {
    if (!phone) {
      return '';
    }
    return FormUtils.formatPhoneNumber(phone);
  }

  clearSearch(searchInput: HTMLInputElement): void {
    this.searchForm.patchValue({ search: '' });
    searchInput.focus();
  }

  onUploadRoster(): void {
    const ref = this.#dialog.open(AlertRosterUploadWizardComponent, {
      panelClass: 'alert-roster-upload-wizard-dialog',
      autoFocus: false,
      disableClose: true,
    });
    ref.componentInstance.setLocations(this.locations() ?? []);
  }

  #subject(): { singular: string; word: string; pluralWord: string } {
    const { singular, plural } = subjectLabels(this.currentOrganization());
    return { singular, word: subjectWord(singular), pluralWord: subjectWord(plural) };
  }

  #offerOpenExistingPatient(patient: Patient, message: string): void {
    const snackRef = this.#snackBar.open(message, 'Open', { duration: 10000 });
    snackRef.onAction().subscribe(() => this.onSelectedPatient(patient));
  }

  #unarchivePatient(patient: Patient): void {
    const { singular, word } = this.#subject();
    this.#patientService
      .updatePatient(patient.id, {
        name: patient.name,
        phone: patient.phone,
        locationId: patient.locationId,
        archived: false,
      })
      .pipe(take(1))
      .subscribe({
        next: (updated) => {
          this.#offerOpenExistingPatient(
            { ...updated, archived: false },
            `${singular} restored from archive. Open the ${word} to alert.`,
          );
          this.#pageIndex = 0;
          this.#pageTokens = [undefined];
          this.loadPatientsPage();
          this.#contactsViewModel.loadProfile();
        },
        error: () => {
          this.#snackBar.open(`Could not restore archived ${word}. Please try again.`, 'Okay', {
            duration: 5000,
          });
        },
      });
  }

  #createNewPatient(result: {
    name: string;
    phone: string;
    location?: { id: string } | null;
  }): void {
    const { word } = this.#subject();
    const patient: Patient = {
      id: uuid(),
      name: result.name,
      phone: (result.phone ?? '').trim(),
      locationId: result.location?.id,
      location: result.location ? (result.location as Patient['location']) : undefined,
    };

    const selfContact = !environment.isEpic ? buildStandalonePatientSelfContact(patient) : null;

    this.#patientService
      .createPatient(patient)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.#contactsViewModel.loadProfile();
          this.#pageIndex = 0;
          this.#pageTokens = [undefined];
          this.loadPatientsPage();

          if (!selfContact) {
            return;
          }

          const previousPatientId = sessionStorage.getItem('patientId');
          sessionStorage.setItem('patientId', patient.id);
          this.#contactService.savePatientContactInformation(selfContact).pipe(take(1)).subscribe({
            next: () => this.#restoreSessionPatientId(previousPatientId),
            error: () => {
              this.#restoreSessionPatientId(previousPatientId);
              this.#snackBar.open(
                `${this.#subject().singular} was added, but the contact could not be saved. Open the ${word} to finish setup.`,
                'Okay',
                { duration: 7000 },
              );
            },
          });
        },
        error: () => {
          this.#snackBar.open(`Could not add ${word}. Please try again.`, 'Okay', { duration: 5000 });
        },
      });
  }

  #restoreSessionPatientId(previousPatientId: string | null): void {
    if (previousPatientId) {
      sessionStorage.setItem('patientId', previousPatientId);
    } else {
      sessionStorage.removeItem('patientId');
    }
  }
}
